import {
  Entries,
  FromEntries,
  Keys,
  MapEntries,
  MapKeys,
  MapValues,
  ObjectCallback,
  ObjectCallbackFlip,
  Values,
} from './object.types'

const mapValuesCurried =
  <T, V1>(func: ObjectCallbackFlip<T, V1>) =>
  (obj: T): Record<keyof T, V1> => {
    const res: Record<keyof T, V1> = {} as Record<keyof T, V1>
    for (const key in obj) {
      res[key] = func(obj[key], key)
    }
    return res
  }

/**
 * Маппим значения внутри объекта:
 * ```ts
 * mapValues((value, key) => `${key}${value}`, { foo: 'bar', baz: 123 }) // => { foo: 'foobar', baz: 'baz123' }
 * ```
 */
// @ts-expect-error всё ещё не может в перегрузку каррированных функций
export const mapValues: MapValues = <T, V1>(func: ObjectCallbackFlip<T, V1>, obj?: T) => {
  if (obj !== undefined) return mapValuesCurried(func)(obj)
  return mapValuesCurried(func)
}

const mapKeysCurried =
  <T, K1 extends PropertyKey>(func: ObjectCallback<T, K1>) =>
  (obj: T): Record<K1, T[keyof T]> => {
    const res = {} as Record<K1, T[keyof T]>
    for (const key in obj) {
      res[func(key, obj[key])] = obj[key]
    }
    return res
  }

/**
 * Маппим ключи внутри объекта:
 * ```ts
 * mapKeys((key, value) => `${key}${value}`, { foo: 'bar', baz: 123 }) // => { foobar: 'foo', baz123: 'baz' }
 * ```
 */
// @ts-expect-error всё ещё не может в перегрузку каррированных функций
export const mapKeys: MapKeys = <T, K1 extends PropertyKey>(func: ObjectCallback<T, K1>, obj?: T) => {
  if (obj !== undefined) return mapKeysCurried(func)(obj)
  return mapKeysCurried(func)
}

const mapEntriesCurried =
  <T, K1 extends PropertyKey, V1>(func: ObjectCallback<T, [K1, V1]>) =>
  (obj: T): Record<K1, V1> => {
    const res = {} as Record<K1, V1>
    for (const key in obj) {
      const [k, v] = func(key, obj[key])
      res[k] = v
    }
    return res
  }

/**
 * Маппим одновременно ключи и значения объекта:
 * ```ts
 * mapKeys((key, value) => [`${key}${value}`, `${value}${key}`], { foo: 'bar', baz: 123 }) // => { foobar: 'barfoo',
 * baz123: '123baz' }
 * ```
 */
// @ts-expect-error всё ещё не может в перегрузку каррированных функций
export const mapEntries: MapEntries = <T, K1 extends PropertyKey, V1>(func: ObjectCallback<T, [K1, V1]>, obj?: T) => {
  if (obj !== undefined) return mapEntriesCurried(func)(obj)
  return mapEntriesCurried(func)
}

/**
 * Получаем список значений в объекте
 */
export const values: Values = Object.values

/**
 * Получаем список ключей в объекте
 */
export const keys: Keys = Object.keys

/**
 * Получаем объект в виде списка пар ключ-значений:
 * ```ts
 * toEntries({ foo: 123, bar: 'bar' }) // => [['foo', 123], ['bar', 'bar']]
 * ```
 */
export const toEntries: Entries = Object.entries

export const fromEntries: FromEntries =
  Object.fromEntries ||
  (<K extends PropertyKey, V>(entries: (readonly [K, V])[] | [K, V][]): Record<K, V> => {
    const res = {} as Record<K, V>
    for (const [k, v] of entries) {
      res[k] = v
    }
    return res
  })
